import { useState, useRef, useCallback } from 'react'
import { useCreateSegment, useDeleteSegment } from '../../hooks/useSegments'
import { generateSegments, processGpxFileWithProgress } from '../../lib/gpx'
import { GpxSummaryDisplay } from './GpxSummary'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import { Button } from '../ui/button'
import { Label } from '../ui/label'
import { RadioGroup, RadioGroupItem } from '../ui/radio-group'
import { Progress } from '../ui/progress'
import { Alert, AlertDescription } from '../ui/alert'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table'
import { Separator } from '../ui/separator'
import { 
  Upload, 
  FileText, 
  AlertTriangle, 
  CheckCircle, 
  Loader2,
  Settings
} from 'lucide-react'
import { GpxSummary, GeneratedSegment, AutoSegmentOptions, Segment } from '../../types'

interface GpxImportDialogProps {
  runId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  existingSegments: Segment[]
}

type ImportStep = 'upload' | 'configure' | 'preview' | 'importing' | 'complete'

export function GpxImportDialog({ runId, open, onOpenChange, existingSegments }: GpxImportDialogProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [currentStep, setCurrentStep] = useState<ImportStep>('upload')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [gpxData, setGpxData] = useState<GpxSummary | null>(null)
  const [generatedSegments, setGeneratedSegments] = useState<GeneratedSegment[]>([])
  const [segmentMode, setSegmentMode] = useState<AutoSegmentOptions['mode']>('every-5km')
  const [processing, setProcessing] = useState(false)
  const [progress, setProgress] = useState(0)
  const [error, setError] = useState<string | null>(null)
  
  const createSegmentMutation = useCreateSegment()
  const deleteSegmentMutation = useDeleteSegment()

  const resetState = () => {
    setCurrentStep('upload')
    setSelectedFile(null)
    setGpxData(null)
    setGeneratedSegments([])
    setSegmentMode('every-5km')
    setProcessing(false)
    setProgress(0)
    setError(null)
  }

  const handleFileSelect = useCallback(async (file: File) => {
    setSelectedFile(file)
    setProcessing(true)
    setError(null)
    setProgress(0)

    try {
      const summary = await processGpxFileWithProgress(file, setProgress)
      setGpxData(summary)
      setCurrentStep('configure')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to process GPX file')
    } finally {
      setProcessing(false)
    }
  }, [])

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      handleFileSelect(file)
    }
  }

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault()
  }

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault()
    const file = event.dataTransfer.files[0]
    if (file && file.name.toLowerCase().endsWith('.gpx')) {
      handleFileSelect(file)
    }
  }

  const handleGeneratePreview = () => {
    if (!gpxData) return

    try {
      const segments = generateSegments(gpxData, { mode: segmentMode })
      setGeneratedSegments(segments)
      setCurrentStep('preview')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate segments')
    }
  }

  const handleImport = async () => {
    if (!generatedSegments.length) return

    setCurrentStep('importing')
    setProgress(0)

    try {
      // Clear existing segments if any
      if (existingSegments.length > 0) {
        for (const segment of existingSegments) {
          await deleteSegmentMutation.mutateAsync(segment.id)
        }
      }

      // Create new segments
      for (let i = 0; i < generatedSegments.length; i++) {
        const segment = generatedSegments[i]
        await createSegmentMutation.mutateAsync({
          run_id: runId,
          label: segment.label,
          distance_km: segment.distance_km,
          elev_gain_m: segment.elev_gain_m,
          elev_loss_m: segment.elev_loss_m,
          order_index: segment.order_index,
          planned_stop_min: 0
        })
        
        setProgress(Math.round(((i + 1) / generatedSegments.length) * 100))
      }

      setCurrentStep('complete')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to import segments')
      setCurrentStep('preview')
    }
  }

  const handleClose = () => {
    onOpenChange(false)
    setTimeout(resetState, 300) // Reset after dialog closes
  }

  const renderUploadStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <p className="text-sm text-gray-600 mb-4">
          Upload a GPX file to automatically create course segments with distance and elevation data.
        </p>
        
        <div
          className="border-2 border-dashed border-gray-300 rounded-lg p-8 hover:border-gray-400 transition-colors cursor-pointer"
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-lg font-medium text-gray-600 mb-2">
            Choose a GPX file or drag it here
          </p>
          <p className="text-sm text-gray-500">
            Maximum file size: 50MB
          </p>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept=".gpx"
          onChange={handleFileInputChange}
          className="hidden"
        />
      </div>

      {selectedFile && processing && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            <span className="text-sm font-medium">{selectedFile.name}</span>
            <Loader2 className="h-4 w-4 animate-spin" />
          </div>
          <Progress value={progress} className="w-full" />
          <p className="text-xs text-gray-500 text-center">
            Processing GPX file... {progress}%
          </p>
        </div>
      )}
    </div>
  )

  const renderConfigureStep = () => (
    <div className="space-y-6">
      {gpxData && (
        <GpxSummaryDisplay gpxData={gpxData} />
      )}

      <Separator />

      <div className="space-y-4">
        <div>
          <Label className="text-base font-medium">Auto-Segmentation Mode</Label>
          <p className="text-sm text-gray-600 mb-3">
            Choose how to split your course into segments:
          </p>
        </div>

        <RadioGroup value={segmentMode} onValueChange={(value) => setSegmentMode(value as AutoSegmentOptions['mode'])}>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="every-1km" id="1km" />
            <label htmlFor="1km" className="text-sm cursor-pointer">
              Every 1 km - Most detailed segmentation
            </label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="every-5km" id="5km" />
            <label htmlFor="5km" className="text-sm cursor-pointer">
              Every 5 km - Balanced detail (recommended)
            </label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="every-10km" id="10km" />
            <label htmlFor="10km" className="text-sm cursor-pointer">
              Every 10 km - Fewer, longer segments
            </label>
          </div>
        </RadioGroup>
      </div>

      {existingSegments.length > 0 && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            This will replace your existing {existingSegments.length} segments. 
            This action cannot be undone.
          </AlertDescription>
        </Alert>
      )}

      <div className="flex gap-2 justify-end">
        <Button variant="outline" onClick={handleClose}>
          Cancel
        </Button>
        <Button onClick={handleGeneratePreview} disabled={!gpxData}>
          <Settings className="h-4 w-4 mr-2" />
          Generate Preview
        </Button>
      </div>
    </div>
  )

  const renderPreviewStep = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium">Generated Segments</h3>
          <p className="text-sm text-gray-600">
            {generatedSegments.length} segments will be created from your GPX file
          </p>
        </div>
        <Button
          variant="outline" 
          size="sm"
          onClick={() => setCurrentStep('configure')}
        >
          <Settings className="h-4 w-4 mr-1" />
          Change Settings
        </Button>
      </div>

      <div className="max-h-64 overflow-y-auto border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Label</TableHead>
              <TableHead>Distance</TableHead>
              <TableHead>Elev Gain</TableHead>
              <TableHead>Elev Loss</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {generatedSegments.map((segment, index) => (
              <TableRow key={index}>
                <TableCell>{segment.label}</TableCell>
                <TableCell>{segment.distance_km} km</TableCell>
                <TableCell>+{segment.elev_gain_m}m</TableCell>
                <TableCell>-{segment.elev_loss_m}m</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="flex gap-2 justify-end">
        <Button variant="outline" onClick={handleClose}>
          Cancel
        </Button>
        <Button onClick={handleImport} disabled={createSegmentMutation.isPending}>
          {createSegmentMutation.isPending ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Importing...
            </>
          ) : (
            <>
              <CheckCircle className="h-4 w-4 mr-2" />
              Import Segments
            </>
          )}
        </Button>
      </div>
    </div>
  )

  const renderImportingStep = () => (
    <div className="space-y-6 text-center">
      <div className="space-y-3">
        <Loader2 className="h-12 w-12 text-blue-600 mx-auto animate-spin" />
        <h3 className="text-lg font-medium">Importing Segments</h3>
        <p className="text-sm text-gray-600">
          Creating {generatedSegments.length} segments from your GPX file...
        </p>
      </div>
      
      <Progress value={progress} className="w-full" />
      <p className="text-xs text-gray-500">
        {progress}% complete
      </p>
    </div>
  )

  const renderCompleteStep = () => (
    <div className="space-y-6 text-center">
      <div className="space-y-3">
        <CheckCircle className="h-12 w-12 text-green-600 mx-auto" />
        <h3 className="text-lg font-medium">Import Complete!</h3>
        <p className="text-sm text-gray-600">
          Successfully created {generatedSegments.length} segments from your GPX file.
        </p>
      </div>

      <div className="flex justify-end">
        <Button onClick={handleClose}>
          Close
        </Button>
      </div>
    </div>
  )

  const getStepContent = () => {
    switch (currentStep) {
      case 'upload':
        return renderUploadStep()
      case 'configure':
        return renderConfigureStep()
      case 'preview':
        return renderPreviewStep()
      case 'importing':
        return renderImportingStep()
      case 'complete':
        return renderCompleteStep()
      default:
        return renderUploadStep()
    }
  }

  const getDialogTitle = () => {
    switch (currentStep) {
      case 'upload':
        return 'Import GPX File'
      case 'configure':
        return 'Configure Segmentation'
      case 'preview':
        return 'Preview Segments'
      case 'importing':
        return 'Importing Segments'
      case 'complete':
        return 'Import Complete'
      default:
        return 'Import GPX File'
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {getDialogTitle()}
          </DialogTitle>
          <DialogDescription>
            {currentStep === 'upload' && 'Upload a GPX file to automatically create course segments'}
            {currentStep === 'configure' && 'Choose how to split your course into segments'}
            {currentStep === 'preview' && 'Review the segments that will be created'}
            {currentStep === 'importing' && 'Creating segments from your GPX data'}
            {currentStep === 'complete' && 'Your GPX file has been successfully imported'}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {getStepContent()}
      </DialogContent>
    </Dialog>
  )
}